"use client";

import React from "react";
import { Activity, ArrowRight, ChevronRight, CircleCheck as CheckCircle2, Cpu, FileCheck, Gauge, Layers, Radio, Sparkles, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const processMetrics = [{
  label: "Cycle Time Reduction",
  value: "Immediate",
  subtext: "in-line feedback vs. offline sampling"
}, {
  label: "Measurement Latency",
  value: "< 2.5s",
  subtext: "real-time spectral feedback"
}, {
  label: "Data Integrity Focus",
  value: "Audit Trail Support",
  subtext: "data integrity & audit trail"
}];
const telemetryPoints = [{
  name: "Continuous Flow Rate",
  value: "In-Line Tracked",
  status: "Optimal"
}, {
  name: "Optical Density (OD)",
  value: "Calibrated Trend",
  status: "Active"
}, {
  name: "Spectral Wavelength",
  value: "1100 - 2450 nm",
  status: "Scanning"
}];
export default function ProcessAnalytics() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    initial: shouldReduceMotion ? {
      opacity: 1
    } : {
      opacity: 0,
      y: 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  };
  return <section id="heroprocessanalytics" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-20 lg:py-28">
      {/* Subtle background technical grid */}
      <div className="pointer-events-none absolute inset-0 opacity-10 bg-[linear-gradient(to_right,hsl(var(--sidebar-border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--sidebar-border))_1px,transparent_1px)] bg-[size:3.5rem_3.5rem]" aria-hidden="true" />

      <div className="relative page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left Column (8 cols): Content */}
          <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={fadeIn.transition} className="lg:col-span-8 flex flex-col items-start text-left">
            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-sidebar-foreground/70">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-sidebar-foreground underline-offset-4 hover:underline">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-sidebar-foreground/40">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/SolutionsPage" className="transition-colors hover:text-sidebar-foreground underline-offset-4 hover:underline">
                    Solutions
                  </Link>
                </li>
                <li aria-hidden="true" className="text-sidebar-foreground/40">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-sidebar-foreground" aria-current="page">
                    Process Analytical Technology
                  </span>
                </li>
              </ol>
            </nav>

            <div className="flex items-center gap-2 mb-4">
              <Badge variant="default" className="bg-primary/20 text-primary border border-primary/30 uppercase tracking-wider font-semibold text-xs px-3 py-1">
                <Radio className="size-3 text-primary animate-pulse mr-1" />
                PROCESS ANALYTICAL TECHNOLOGY
              </Badge>
              <span className="text-xs text-sidebar-foreground/70 hidden sm:inline-flex items-center gap-1.5">
                <span className="inline-block size-2 rounded-full bg-emerald-500" />
                Regulated Manufacturing Environments
              </span>
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-sidebar-foreground leading-[1.15] mb-6">
              Offline laboratory turnaround delays critical batch release
              decisions, leaving production vulnerable to out-of-spec batches.
            </h1>

            <p className="text-lg sm:text-xl text-sidebar-foreground/80 leading-relaxed max-w-3xl mb-8">
              Real-time in-line and at-line analytics enable immediate process
              control feedback, ensuring continuous quality assurance and
              immediate deviation response across regulated manufacturing lines.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-10">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="bg-sidebar-accent text-sidebar-foreground border-sidebar-border hover:bg-sidebar-accent/80 font-medium px-6">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Request a Demonstration</span>
                </Link>
              </Button>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full pt-6 border-t border-sidebar-border">
              {processMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col">
                  <span className="text-2xl lg:text-3xl font-bold text-sidebar-foreground">
                    {metric.value}
                  </span>
                  <span className="text-sm font-medium text-primary mt-0.5">
                    {metric.label}
                  </span>
                  <span className="text-xs text-sidebar-foreground/70">
                    {metric.subtext}
                  </span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column (4 cols): Technical Schematic Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-4">
            <Card className="bg-sidebar-accent/60 border-sidebar-border text-sidebar-foreground backdrop-blur-md shadow-xl overflow-hidden">
              <CardHeader className="pb-3 border-b border-sidebar-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Cpu className="size-4 text-primary" />
                    <CardTitle className="text-base font-semibold text-sidebar-foreground">
                      In-Line Probe Flow Schematic
                    </CardTitle>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/50">
                    <span className="size-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    STREAM LIVE
                  </span>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 pt-4">
                {/* SVG Schematic Graphic */}
                <div className="relative rounded-lg border border-sidebar-border/70 bg-sidebar/90 p-3.5 flex flex-col items-center">
                  <svg viewBox="0 0 320 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto text-sidebar-foreground" aria-label="Process Analytical Technology In-Line Flow Probe Diagram">
                    {/* Sanitary Pipe section */}
                    <rect x="20" y="70" width="280" height="50" rx="6" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
                    
                    {/* Flow markers */}
                    <path d="M 40 95 L 110 95" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 4" />
                    <polygon points="110,91 120,95 110,99" fill="hsl(var(--primary))" />

                    <path d="M 200 95 L 270 95" stroke="hsl(var(--primary))" strokeWidth="2" strokeDasharray="4 4" />
                    <polygon points="270,91 280,95 270,99" fill="hsl(var(--primary))" />

                    {/* In-line Probe Housing */}
                    <rect x="142" y="15" width="36" height="88" rx="4" fill="currentColor" fillOpacity="0.15" stroke="hsl(var(--primary))" strokeWidth="2" />
                    
                    {/* Flange clamp */}
                    <rect x="134" y="64" width="52" height="12" rx="2" fill="currentColor" fillOpacity="0.3" stroke="currentColor" strokeOpacity="0.4" />

                    {/* Sensor Tip / Focal Point */}
                    <circle cx="160" cy="95" r="7" fill="hsl(var(--primary))" fillOpacity="0.2" stroke="hsl(var(--primary))" strokeWidth="2" />
                    <circle cx="160" cy="95" r="3" fill="hsl(var(--primary))" />

                    {/* Pulse Emission waves */}
                    <path d="M 148 95 C 148 88, 172 88, 172 95" stroke="hsl(var(--primary))" strokeWidth="1.5" strokeOpacity="0.8" />
                    <path d="M 142 95 C 142 82, 178 82, 178 95" stroke="hsl(var(--primary))" strokeWidth="1" strokeOpacity="0.5" />

                    {/* Labels */}
                    <text x="35" y="62" fill="currentColor" fillOpacity="0.7" fontSize="9" fontFamily="monospace">
                      Process Inflow
                    </text>
                    <text x="215" y="62" fill="currentColor" fillOpacity="0.7" fontSize="9" fontFamily="monospace">
                      Continuous Out
                    </text>
                    <text x="186" y="32" fill="hsl(var(--primary))" fontSize="9" fontWeight="bold" fontFamily="monospace">
                      PAT Probe (NIR/Raman)
                    </text>
                  </svg>
                  
                  <div className="w-full flex items-center justify-between text-[11px] font-mono text-sidebar-foreground/70 mt-2 px-1">
                    <span>Flow: Sanitary Tri-Clamp</span>
                    <span className="text-primary">Optical Path: 2.0 mm</span>
                  </div>
                </div>

                {/* Telemetry rows */}
                <div className="space-y-2">
                  {telemetryPoints.map((point, i) => <div key={point.name} data-index={i} className="flex items-center justify-between text-xs p-2 rounded bg-sidebar/50 border border-sidebar-border/40">
                      <span className="text-sidebar-foreground/80 font-medium">
                        {point.name}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-sidebar-foreground">
                          {point.value}
                        </span>
                        <span className="text-[10px] text-emerald-400 bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-900/60">
                          {point.status}
                        </span>
                      </div>
                    </div>)}
                </div>

                {/* Spec release summary */}
                <div className="pt-2 border-t border-sidebar-border/60 flex items-center justify-between text-xs text-sidebar-foreground/70">
                  <span className="flex items-center gap-1 text-emerald-400 font-medium">
                    <CheckCircle2 className="size-3.5" />
                    In-Spec Production State
                  </span>
                  <span className="font-mono">Sync: 100ms</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}